import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment, { Moment } from 'moment/moment';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';

import {
  OverdueTaskTableFieldsFragment,
  TaskDetailViewFieldsFragment,
  TaskStatus,
  TaskType,
  UpdateOperationsTaskMutationVariables
} from '../../../../generated/graphql';
import { StandupDashboardServiceService } from '../standup-dashboard-service.service';
import { TaskServiceService } from '../../../planner/task-service.service';
import { PusherService } from '../../../pusher/pusher.service';
import { NewTaskDialogComponent } from '../../../planner/new-task-dialog/new-task-dialog.component';
import { ProjectManagerGQL } from '../../graphql';
import { UserProfile } from '../../../graphql/graphql';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-overdue-tasks-table',
  templateUrl: './overdue-tasks-table.component.html',
  styleUrls: ['./overdue-tasks-table.component.scss']
})
export class OverdueTasksTableComponent implements OnInit, OnDestroy {
  private pusherSubscription: Subscription;
  tableData: MatTableDataSource<OverdueTaskTableFieldsFragment> = new MatTableDataSource([]);
  displayedColumns: Iterable<string> = [
    'manager', 'planned_date', 'status', 'description', 'nextAction', 'metric', 'reasonComment', 'metricDate', 'client', 'candidate', 'overdue_count'
  ];
  displayOverdueOnly = true;
  isLoading = true;
  displayOnlyImportant = new FormControl(false);
  hideRepeatedIssues = new FormControl(false);
  ownerFilterCtrl = new FormControl('');
  selectedOwners: UserProfile[] = [];
  userProfiles: UserProfile[] = [];
  owners = new FormControl([]);
  isAllManagers = false;
  isRefreshing = false;

  repeatedTasksSet = {};
  defaultMetricDates = {
    dateFrom: this.getLastWorkingDay(moment()),
    dateTo: moment()
  };

  @Input() dashboardId: any;
  @Input() cardDescription: string;
  @Input() filterTaskCategoriesKeys: string;
  @Input() excludeTaskCategoriesKeys: string;
  @Input() excludeStatuses: string;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private dashboardService: StandupDashboardServiceService,
    private tasksService: TaskServiceService,
    private pusherService: PusherService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private location: Location,
    private projectManagerGQL: ProjectManagerGQL
  ) { }

  ngOnInit(): void {
    this.dashboardService.changedMetricDatesSubject.subscribe(dates => {
      this.defaultMetricDates = dates;
      if (this.tableData?.data?.length) {
        this.tableData.data.map(v => {
          this.repeatedTasksSet[v.id] = v.metricvalueSet?.edges.some(edge =>
            moment(edge.node.date).isBetween(dates.dateFrom, dates.dateTo, 'day', '[]')
          ) || false;
        });
      }
    });
    this.loadOverdueTasks(this.displayOverdueOnly, this.displayOnlyImportant.value, this.owners.value);
    this.connectPusherTaskUpdate();
    this.getUserProfiles();
    this.setOwnerFilterListener();
    this.tableData.sort = this.sort;
    this.dashboardService.changedTaskSubject.subscribe(value => {
      if (this.tableData?.data) {
        let isFound = false;
        // @ts-ignore
        this.tableData?.data = this.tableData?.data.map(task => {
          if (task.id === value.id) {
            isFound = true;
            return value;
          } else {
            return task;
          }
        });
        if (!isFound && value?.dashboards?.edges?.some(item => item?.node?.id === this.dashboardId)) {
          this.refreshTable();
        }
      }
    });
  }

  getLastWorkingDay(date) {
    let lastWorkingDay;

    switch (date.isoWeekday()) {
      case 1:
        lastWorkingDay = date.subtract(3, 'days');
        break;
      case 6:
        lastWorkingDay = date.subtract(1, 'days');
        break;
      case 7:
        lastWorkingDay = date.subtract(2, 'days');
        break;
      default:
        lastWorkingDay = date.subtract(1, 'days');
        break;
    }

    return lastWorkingDay;
  }

  getUserProfiles() {
    this.projectManagerGQL.fetch({ showInactive: false }).subscribe(res => {
      const profiles = [...res.data.userprofiles];
      if (!profiles.find(profile => profile.id === res.data.currentUser.id)) {
        profiles.push(res.data.currentUser);
      }
      this.userProfiles = profiles;
      this.selectedOwners = [...this.userProfiles];
    });
  }

  searchOwners() {
    let filter = this.ownerFilterCtrl.value?.toLowerCase() || '';
    this.selectedOwners = this.userProfiles.filter((option) => {
      return (
        (option?.user?.firstName?.toLowerCase() + ' ' + option?.user?.lastName?.toLowerCase()).includes(filter)
      );
    });
  }

  setOwnerFilterListener() {
    this.ownerFilterCtrl.valueChanges.subscribe(() => {
      this.searchOwners();
    });
  }

  loadOverdueTasks(overdueOnly?: boolean, isImportantOnly?: boolean, owners: string[] = []): void {
    const filteredOwners = owners.filter(owner => owner !== undefined);
    this.isLoading = true;
    this.tasksService.getOverdueTasks(
      this.dashboardId,
      this.filterTaskCategoriesKeys,
      this.excludeTaskCategoriesKeys,
      this.excludeStatuses,
      overdueOnly,
      isImportantOnly,
      this.hideRepeatedIssues.value,
      null,
      filteredOwners
    ).subscribe(
      data => {
        if (data.data?.tasks) {
          this.tableData.data = data.data.tasks.edges.map(edge => {
            return {
              ...edge.node,
              reasonSet: this.getCurrentMetricValue(edge.node)
                ? this.dashboardService.groupCommentsByReasonSet(this.getCurrentMetricValue(edge.node).metricvaluecommentSet as any)
                : null
            };
          });
          this.tableData.sort = this.sort;
        }
        if (!Object.keys(this.repeatedTasksSet).length) {
          if (this.tableData?.data?.length) {
            this.tableData.data.map(v => {
              this.repeatedTasksSet[v.id] = v.metricvalueSet?.edges.some(edge =>
                moment(edge.node.date).isBetween(this.defaultMetricDates.dateFrom, this.defaultMetricDates.dateTo, 'day', '[]')
              ) || false;
            });
          }
        }
        this.isRefreshing = false;
        this.isLoading = false;
      },
      () => this.isLoading = false
    );
  }

  connectPusherTaskUpdate() {
    this.pusherSubscription = this.pusherService.dashboardTaskUpdateEmitter.subscribe(event => {
      const taskIdx = this.tableData.data.findIndex(item => item.id === event.task_id);

      if (taskIdx > -1) {
        this.dashboardService.getUpdatedTask(event.task_id).subscribe(upTask => {
          this.tableData[taskIdx] = {
            ...this.tableData[taskIdx],
            date: upTask.date,
            status: upTask.status,
            comment: upTask.comment
          };
          this.tableData.data = [ ...this.tableData.data ];
        });
      }
    });
  }

  onDateChange(date: Moment, task: TaskType) {
    const newDate = moment(date).format('YYYY-MM-DD');
    if (task.date !== newDate) {
      this.updateTask({ taskId: task.id, date: newDate }, task);
    }
  }

  onStatusChange($event: TaskStatus, task: any, updateImportance?: boolean) {
    if (updateImportance) {
      this.updateTask({ taskId: task.id, status: $event, isImportant: !task.isImportant}, task);
      task.isImportant = !task.isImportant;
    } else {
      this.updateTask({ taskId: task.id, status: $event }, task);
    }
  }

  updateTask(updatesTask: UpdateOperationsTaskMutationVariables, task?: TaskType) {
    this.tasksService.updateTaskPartial(updatesTask).subscribe(
      resp => {
        task.date = resp.data.updateOperationsTask.task.date;
        task.status = resp.data.updateOperationsTask.task.status;
        this.dashboardService.changedTaskSubject.next(resp.data.updateOperationsTask.task);
      }, () => { }
    );
  }

  ngOnDestroy() {
    if (this.pusherSubscription) {
      this.pusherSubscription.unsubscribe();
    }
  }

  openTaskDialog(task: TaskDetailViewFieldsFragment) {
    const config: MatDialogConfig<any> = {
      data: { task },
      panelClass: 'rounded-dialog-16',
      width: '600px',
      height: '640px',
      autoFocus: false
    };
    if (task) {
      this.location.replaceState(`/standups/${encodeURIComponent(task.id)}`);
    }
    this.dialog.open(NewTaskDialogComponent, config).afterClosed().subscribe(value => {
      if (value?.delete) {
        this.snackBar.open(
          this.translate.instant('DELETED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' }
        );
        this.loadOverdueTasks(this.displayOverdueOnly);
      } else if (value?.id) {
        task.date = value.date;
        task.status = value.status;
        task.comment = value.comment;
        task.dashboard = value.dashboard;
        this.dashboardService.changedTaskSubject.next(value);
        this.snackBar.open(
          this.translate.instant('UPDATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' }
        );
      }
      this.location.replaceState('/standups');
    });
  }

  getOverdueStatusColor(date) {
    if (moment(date).isSame(moment(), 'day')) {
      return 'warn';
    } else if (moment(date).diff(moment(), 'day') < 0) {
      return 'alert';
    } else {
      return 'regular';
    }
  }

  getCurrentMetricValue(element: OverdueTaskTableFieldsFragment) {
    return element.metricvalueSet?.edges.length ? element.metricvalueSet.edges[0].node : element.metricvalueLevel2.edges[0]?.node;
  }

  openDescription(template: TemplateRef<any>) {
    this.dialog.open(template, { autoFocus: false });
  }

  formatInnerText(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  refreshTable() {
    if (!this.isRefreshing || !this.isLoading) {
      this.isRefreshing = true;
      this.tableData = new MatTableDataSource([]);
      this.loadOverdueTasks(this.displayOverdueOnly, this.displayOnlyImportant.value, this.owners.value)
    }
  }

  prepareCommentValue(comment: string): string {
    if (!comment) return comment;

    let count = 0;
    return comment.replace(/\n/g, (match) => {
      count++;
      return count <= 5 ? '<br/>' : match;
    });
  }
}
